import { inject, Injectable, signal, Signal } from '@angular/core';
import * as Y from 'yjs';
import { type Awareness } from 'y-protocols/awareness';
import { HubConnection } from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { YjsSignalRProvider } from 'shared/util-yjs-signalr';
import { ClientType } from '@pcp/core/interfaces';
import { LessonYjsBinding } from './lesson-yjs-binding';
import { DisplayStateSnapshot, LessonStatus, PrimarySlideHistoryEntry, SyncedLessonState } from '../../public/yjs/lesson-ydoc-schema';
import { PCP_LOGGER, PcpLoggerInterface } from '@pcp/logging';
import { LessonYjsApiService } from './lesson-yjs-api.service';
import { LESSON_YJS_CLIENT_TYPE } from '../../public/yjs/lesson-yjs-client-type.token';

/**
 * LessonYjsService — Yjs-primary lesson state synchronization.
 *
 * @internal Use `ClassroomFacade` for the public API.
 *
 * Yjs is the source of truth for synced properties.
 * Write methods modify the Y.Doc directly. A Yjs observer emits ALL changes
 * (local + remote) via `changes$`, which the mirror effect dispatches to NgRx.
 *
 * Responsibilities:
 * - Create/destroy Y.Doc, YjsSignalRProvider, and LessonYjsBinding
 * - Expose granular write methods with permission + init guards
 * - Expose `changes$` / `primarySlideHistory$` / `mutationBatchAdded$` for the facade
 * - Manage hydration state (controls when subscribers start receiving emissions)
 *
 * **Display-state handoff:** the Classroom Y.Doc is swapped, not mutated, at every
 * lesson-lifecycle flip (`documentId = lesson-state:${lessonId}` — one lesson is one
 * document). Display state survives the swap via the schema-driven
 * `LessonYjsBinding.exportDisplayState()` / `importDisplayState()` pair, in BOTH
 * directions: draft → lesson in `initialize({ isFreshLesson: true })` and
 * lesson → draft in `initializeOffline(seed)`. Lesson-scoped state (lifecycle flags,
 * `LESSON_SCOPED_KEYS`; primary-slide history) never crosses the boundary. Both
 * imports run BEFORE hydration — see the binding's `importDisplayState` docs.
 */
@Injectable({ providedIn: 'root' })
export class LessonYjsService {
    private readonly clientType: ClientType = inject(LESSON_YJS_CLIENT_TYPE);
    private readonly lessonYjsApi = inject(LessonYjsApiService);
    private readonly log: PcpLoggerInterface = inject(PCP_LOGGER).createScoped('yjs');

    /**
     * True iff this client is allowed to write to the Classroom Y.Doc.
     * Permission is role-based and constant per app: only `Teacher` and `Beamer`
     * are writers. The value is derived from the injected `LESSON_YJS_CLIENT_TYPE`
     * once at construction; it does not change at runtime.
     *
     * Exposed as a `Signal` so UI gates and `computed()`s can depend on it
     * directly. `guardWrite()` reads it on every write attempt.
     */
    readonly isWriter: Signal<boolean> = signal(
        this.clientType === 'Teacher' || this.clientType === 'Beamer'
    );

    private yDoc: Y.Doc | null = null;
    private provider: YjsSignalRProvider | null = null;
    private binding: LessonYjsBinding | null = null;

    /**
     * The lesson the current document is synced for, or `null` in offline/draft
     * mode and after `destroy()`. Backs `isSyncedToLesson()` — kept private so the
     * raw id never widens the public surface.
     */
    private syncedLessonId: string | null = null;

    /** Hydration: observer effect only dispatches to NgRx when this is true */
    private readonly hydratedSubject = new BehaviorSubject<boolean>(false);
    readonly hydrated$: Observable<boolean> = this.hydratedSubject.asObservable();

    /** Observable of state changes from Yjs (local + remote) */
    get changes$(): Observable<Partial<SyncedLessonState>> | null {
        return this.binding?.changes$ ?? null;
    }

    /** Observable of new DomMutation batches added to any layer's Y.Array (remote only — local writes are suppressed; see `LessonYjsBinding.mutationBatchAdded$`). */
    get mutationBatchAdded$(): Observable<{ layer: 1 | 2 | 3; batch: string }> | null {
        return this.binding?.mutationBatchAdded$ ?? null;
    }

    /** Observable of `clearMutations()` events that emptied a layer's Y.Array — symmetric on writer + receiver, slide-identity suppression in the binding observer. See `LessonYjsBinding.mutationBatchesCleared$`. */
    get mutationBatchesCleared$(): Observable<{ layer: 1 | 2 | 3 }> | null {
        return this.binding?.mutationBatchesCleared$ ?? null;
    }

    /** Observable of dynamic-content items appended to a layer's Y.Array (symmetric on writer + receiver). See `LessonYjsBinding.dynamicContentAdded$`. */
    get dynamicContentAdded$(): Observable<{ layer: 1 | 2 | 3; itemJson: string }> | null {
        return this.binding?.dynamicContentAdded$ ?? null;
    }

    /** Observable of `clearDynamicContent()` events that emptied a layer's Y.Array (symmetric on writer + receiver; suppressed on slide-identity cascades). See `LessonYjsBinding.dynamicContentCleared$`. */
    get dynamicContentCleared$(): Observable<{ layer: 1 | 2 | 3 }> | null {
        return this.binding?.dynamicContentCleared$ ?? null;
    }

    /** Observable of the full primary-slide history snapshot (local + remote). */
    get primarySlideHistory$(): Observable<PrimarySlideHistoryEntry[]> | null {
        return this.binding?.primarySlideHistory$ ?? null;
    }

    /**
     * Initialize Y.Doc, provider, and binding for a lesson.
     *
     * For a fresh lesson (teacher), call with `isFreshLesson: true` to write defaults
     * and upgrade `lessonStatus` from `'draft'` to `'active'`.
     * For restore/reconnect or beamer join, defaults come from Redis via provider.
     *
     * **Draft → lesson handoff (fresh teacher start):** the teacher's pre-lesson
     * display state lives in the offline draft Y.Doc. It is exported here BEFORE
     * `destroy()` via the schema-driven `exportDisplayState()` (everything except
     * `LESSON_SCOPED_KEYS` and the primary-slide history) and imported into the fresh
     * synced Y.Doc before hydration. The reverse handoff (lesson → draft on stop) uses
     * the same pair via `initializeOffline(seed)`.
     *
     * `options.resignUrls` re-signs the carried snapshot's URL-bearing payloads with
     * the importing client's current SAS between export and import. It is a
     * caller-supplied seam because this service holds no Store and the binding is
     * deliberately Angular-free — the lifecycle effects, which have the Store, read
     * `fromAuth.selectSas` and supply the transform.
     *
     * `options.awarenessSendEnabled$` closes the ephemeral broadcast channel when the
     * lesson is no longer running. Same seam and same reason as `resignUrls`: the
     * lifecycle effects hold the Store. It gates awareness only — never the Y.Doc
     * channel, because a dropped document update is never retried.
     */
    async initialize(
        lessonId: string,
        hubConnection: HubConnection,
        options?: {
            isFreshLesson?: boolean;
            resignUrls?: (snapshot: DisplayStateSnapshot) => DisplayStateSnapshot;
            awarenessSendEnabled$?: Observable<boolean>;
        }
    ): Promise<void> {
        // Export the draft's display state before tearing down, but only when transitioning
        // offline draft → fresh active lesson. Other transitions (reconnect / beamer join)
        // restore from Redis and must not be polluted by local draft data.
        const isFreshTeacherStart = options?.isFreshLesson === true && this.clientType === 'Teacher';
        const draftDisplayState = isFreshTeacherStart
            ? this.binding?.exportDisplayState() ?? null
            : null;
        // The lesson-start seed entry needs the lection of the slide being carried, and the
        // carried slide cannot yield it (a slide knows only `parentId`). The draft document's
        // last history entry describes exactly that slide, so read the lection off it here —
        // before `destroy()` below takes the draft document away. Null when the draft recorded
        // nothing, which leaves the seed entry unattributed rather than wrong.
        const draftLectionId = isFreshTeacherStart
            ? this.binding?.getPrimarySlideHistorySnapshot().at(-1)?.lectionId ?? null
            : null;

        this.destroy();

        try {
            const documentId = `lesson-state:${lessonId}`;
            const fetchInitialState = () =>
                this.lessonYjsApi.fetchLessonYjsState(lessonId, documentId);

            this.yDoc = new Y.Doc();
            this.binding = new LessonYjsBinding(this.yDoc, this.log);
            this.syncedLessonId = lessonId;
            this.provider = new YjsSignalRProvider(documentId, this.yDoc, hubConnection, {
                isTeacher: this.clientType === 'Teacher',
                fetchInitialState: options?.isFreshLesson ? undefined : fetchInitialState,
                logger: this.log,
                // Required for the canvas drawing migration: intra-stroke live preview is
                // streamed via awareness (the `drawingStroke` ephemeral field), like PT's
                // vehicle-drag. The LessonHub implements SendAwarenessUpdate /
                // ReceiveAwarenessUpdate (StreamingHub.LessonHub.cs).
                enableAwareness: true,
                // Mirror the hub's own authorization: `SendAwarenessUpdate` is
                // `[Authorize("IsTeacherOrBeamerClient")]`, so a non-writer that sends is
                // rejected — once per 15s keep-alive, for the whole lesson. Receive stays
                // wired for every role, so followers still render the highlight cursor.
                canSendAwareness: this.isWriter(),
                awarenessSendEnabled$: options?.awarenessSendEnabled$,
            });

            // Neutralise the Awareness constructor's seeded `setLocalState({})` BEFORE connect()
            // attaches the update handler, so the seed is never broadcast and y-protocols has
            // nothing to renew. Must happen here, at document scope: tying it to drawing-layer
            // binding left a teacher or beamer that has no slide canvas mounted (beamer pairing
            // screen, workspace with no slide shown) heartbeating for the whole lesson —
            // measured 2026-09-09 at ~1 frame/15s on both.
            this.provider.awareness?.setLocalState(null);

            this.provider.connect();

            if (isFreshTeacherStart) {
                // Order is load-bearing: defaults → import → hydration → history seed.
                // The import must complete BEFORE `hydratedSubject.next(true)` because
                // (1) the hydration edge triggers the fabric rebind, whose `syncFromYjs()`
                //     must reconcile against already-populated drawing arrays — clearing
                //     stale untracked strokes from the destroyed doc and re-adding carried
                //     ones as tracked, instead of leaving a duplicate; and
                // (2) the facade's `changes$` prepends `startWith(getSnapshot())` on the
                //     hydration edge, so importing first carries the full state to the
                //     display Signals in that single emission.
                // `setLessonStatus('active')` cannot be overwritten by the import —
                // `lessonStatus` is in `LESSON_SCOPED_KEYS` and never carried.
                this.binding.initializeDefaults();
                this.binding.setLessonStatus('active');
                if (draftDisplayState !== null) {
                    const snapshot = options?.resignUrls?.(draftDisplayState) ?? draftDisplayState;
                    // START-EDGE EXCEPTION: `activeExtensionApp` is display state and
                    // carries across the STOP handoff, but is deliberately dropped on
                    // lesson START. An extension app open pre-lesson holds its own,
                    // local-only Y.Doc (e.g. PracticeTrainer), and that second document
                    // does not hand off — carrying "open" into the lesson would show an
                    // app whose canvas state never syncs to the beamer. Dropping the key
                    // closes the app on start (defaults leave it null); reopening it
                    // creates a fresh, synced app document. Interim behaviour until the
                    // app document gets its own takeover — see OpenSpec change
                    // `practice-trainer-state-takeover`.
                    const lessonMap = { ...snapshot.lessonMap };
                    delete lessonMap['activeExtensionApp'];
                    this.binding.importDisplayState({ ...snapshot, lessonMap });
                }
                this.hydratedSubject.next(true);
                // History never crosses a handoff — re-derive the lesson-start seed entry
                // from the carried primary slide.
                this.binding.seedPrimarySlideHistoryFromCurrentSlide(draftLectionId);
            } else {
                // Wait for state from server (late joiner / restore)
                const hasState = await this.provider.waitForInitialSync();
                if (!hasState) {
                    this.log.warn('No state received from server — Y.Doc may be empty');
                }
                // Enable hydration after state received — observer will fire and dispatch to NgRx
                this.hydratedSubject.next(true);
            }
        } catch (err) {
            this.log.error('Initialization failed — cleaning up', err as Error);
            this.destroy();
            throw err;
        }
    }

    /**
     * Initialize a local-only Y.Doc with no SignalR provider attached.
     *
     * Used when the teacher has a module open but no lesson is started yet, and
     * again after a lesson stop (lesson → draft handoff). The Y.Doc accepts writes
     * (e.g. selecting a slide) so the display path is identical to a live lesson,
     * but nothing is synced to other clients.
     *
     * `lessonStatus` stays `'draft'` to distinguish this state from a started
     * lesson. On lesson start, the lifecycle effect will `destroy()` this Y.Doc
     * and call `initialize()` to create a fresh synced one — display state crosses
     * that boundary via the export/import handoff (see `initialize`).
     *
     * **Lesson → draft handoff:** pass `seed` to carry the stopped lesson's display
     * state into the new draft. This method destroys the current document
     * internally, so the CALLER must export first:
     * `exportDisplayState()` → `initializeOffline(snapshot)`. The seed is imported
     * after `initializeDefaults()` and BEFORE hydration (same two reasons as in
     * `initialize`: fabric `syncFromYjs` reconcile; `changes$` `startWith`
     * emission). Lesson-scoped keys are never in the seed, so the draft reports no
     * active lesson, no pause, no pending stop.
     *
     * `resignUrls` re-signs the seed's URL-bearing payloads with the current SAS —
     * caller-supplied for the same reason as in `initialize` (this service is
     * Store-less and SAS-agnostic).
     *
     * Only Teacher clients should call this; other client types have nothing
     * meaningful to do with a local-only Y.Doc.
     */
    initializeOffline(
        seed?: DisplayStateSnapshot,
        resignUrls?: (snapshot: DisplayStateSnapshot) => DisplayStateSnapshot,
    ): void {
        this.destroy();

        try {
            this.yDoc = new Y.Doc();
            this.binding = new LessonYjsBinding(this.yDoc, this.log);
            // No provider — purely local.
            // lessonStatus stays 'draft' (the default). Order is load-bearing:
            // defaults → import → hydration (see `initialize` for the two reasons).
            this.binding.initializeDefaults();
            if (seed !== undefined) {
                this.binding.importDisplayState(resignUrls?.(seed) ?? seed);
            }
            this.hydratedSubject.next(true);
        } catch (err) {
            this.log.error('initializeOffline failed — cleaning up', err as Error);
            this.destroy();
            throw err;
        }
    }

    // --- Write methods (guarded by init + permission) ---

    pauseLesson(): void {
        if (!this.guardWrite('pauseLesson')) return;
        this.binding?.pauseLesson();
    }

    resumeLesson(): void {
        if (!this.guardWrite('resumeLesson')) return;
        this.binding?.resumeLesson();
    }

    setLessonStatus(status: LessonStatus): void {
        if (!this.guardWrite('setLessonStatus')) return;
        this.binding?.setLessonStatus(status);
    }

    setPauseImageUrl(url: string | null): void {
        if (!this.guardWrite('setPauseImageUrl')) return;
        this.binding?.setPauseImageUrl(url);
    }

    showRegistrationCode(): void {
        if (!this.guardWrite('showRegistrationCode')) return;
        this.binding?.showRegistrationCode();
    }

    hideRegistrationCode(): void {
        if (!this.guardWrite('hideRegistrationCode')) return;
        this.binding?.hideRegistrationCode();
    }

    beginLessonStop(): void {
        if (!this.guardWrite('beginLessonStop')) return;
        this.binding?.beginLessonStop();
    }

    endLessonStop(): void {
        if (!this.guardWrite('endLessonStop')) return;
        this.binding?.endLessonStop();
    }

    openExtensionApp(extensionApp: string): void {
        if (!this.guardWrite('openExtensionApp')) return;
        this.binding?.openExtensionApp(extensionApp);
    }

    closeExtensionApp(): void {
        if (!this.guardWrite('closeExtensionApp')) return;
        this.binding?.closeExtensionApp();
    }

    setSlide(
        layer: 1 | 2 | 3,
        slideJson: string,
        isAccessedViaHotspot: boolean,
        historyEntry?: PrimarySlideHistoryEntry
    ): void {
        if (!this.guardWrite('setSlide')) return;
        // History is conceptually teacher-driven: a Beamer re-applying a slide must not re-log it.
        const entryToForward = this.clientType === 'Teacher' ? historyEntry : undefined;
        this.binding?.setSlide(layer, slideJson, isAccessedViaHotspot, entryToForward);
    }

    removeSlide(layer: 2 | 3): void {
        if (!this.guardWrite('removeSlide')) return;
        this.binding?.removeSlide(layer);
    }

    setSlideViewBox(layer: 1 | 2 | 3, viewBox: string): void {
        if (!this.guardWrite('setSlideViewBox')) return;
        this.binding?.setSlideViewBox(layer, viewBox);
    }

    setSlideTextboardPage(layer: 1 | 2 | 3, pageNumber: number): void {
        if (!this.guardWrite('setSlideTextboardPage')) return;
        this.binding?.setSlideTextboardPage(layer, pageNumber);
    }

    setSlideScrollVideoFrame(layer: 1 | 2 | 3, frame: number): void {
        if (!this.guardWrite('setSlideScrollVideoFrame')) return;
        this.binding?.setSlideScrollVideoFrame(layer, frame);
    }

    appendSlideDynamicContent(layer: 1 | 2 | 3, itemJson: string): void {
        if (!this.guardWrite('appendSlideDynamicContent')) return;
        this.binding?.appendSlideDynamicContent(layer, itemJson);
    }

    resetSlideDynamicContent(layer: 1 | 2 | 3): void {
        if (!this.guardWrite('resetSlideDynamicContent')) return;
        this.binding?.resetSlideDynamicContent(layer);
    }

    resetSlideToInitial(layer: 1 | 2 | 3): void {
        if (!this.guardWrite('resetSlideToInitial')) return;
        this.binding?.resetSlideToInitial(layer);
    }

    setNavigationPreviewBasicSlides(json: string): void {
        if (!this.guardWrite('setNavigationPreviewBasicSlides')) return;
        this.binding?.setNavigationPreviewBasicSlides(json);
    }

    setNavigationPreviewNextSlides(json: string): void {
        if (!this.guardWrite('setNavigationPreviewNextSlides')) return;
        this.binding?.setNavigationPreviewNextSlides(json);
    }

    revealPerceptionSlide(layer: 1 | 2 | 3): void {
        if (!this.guardWrite('revealPerceptionSlide')) return;
        this.binding?.revealPerceptionSlide(layer);
    }

    deactivateHotspot(layer: 1 | 2 | 3, index: number): void {
        if (!this.guardWrite('deactivateHotspot')) return;
        this.binding?.deactivateHotspot(layer, index);
    }

    // --- Mutation array methods (Phase 4: DomMutation sync via Yjs) ---

    appendMutationBatch(layer: 1 | 2 | 3, batchJson: string): void {
        if (!this.guardWrite('appendMutationBatch')) return;
        this.binding?.appendMutationBatch(layer, batchJson);
    }

    clearMutations(layer: 1 | 2 | 3): void {
        if (!this.guardWrite('clearMutations')) return;
        this.binding?.clearMutations(layer);
    }

    /** Get all existing mutation batches for a layer (for late-join replay). */
    getMutationBatches(layer: 1 | 2 | 3): string[] {
        return this.binding?.getMutationBatches(layer) ?? [];
    }

    /** Get all existing dynamic content items (raw JSON strings) for a layer (for late-join replay). */
    getDynamicContentSnapshot(layer: 1 | 2 | 3): string[] {
        return this.binding?.getDynamicContentSnapshot(layer) ?? [];
    }

    // --- Drawing-canvas handles (for the data-access-internal LessonDrawingService) ---
    // These expose raw Yjs/awareness handles so the drawing service can construct a
    // FabricYjsBinding over the CURRENT Y.Doc. They stay inside the lib — never on the
    // public facade. Return null before init / between destroy and re-init.

    /** The current per-layer drawing-objects `Y.Array<Y.Map>`, or null if not initialized. */
    getDrawingObjectsArray(layer: 1 | 2 | 3): Y.Array<Y.Map<unknown>> | null {
        return this.binding?.getDrawingObjectsArray(layer) ?? null;
    }

    /**
     * Identity of the layer's current slide *showing* (`slide-showing-identity`), or `''`
     * when nothing has been shown yet / before init. Read (never cached) by
     * `LessonDrawingService` to stamp locally created drawings and reject stale ones.
     */
    getShowingId(layer: 1 | 2 | 3): string {
        return this.binding?.getShowingId(layer) ?? '';
    }

    /** The current per-layer drawing metadata `Y.Map` (FabricYjsBinding requires one), or null. */
    getDrawingMetadataMap(layer: 1 | 2 | 3): Y.Map<unknown> | null {
        return this.binding?.getDrawingMetadataMap(layer) ?? null;
    }

    /** Clear all drawing objects for a layer (slide reset). Guarded like other writes. */
    clearDrawingObjects(layer: 1 | 2 | 3): void {
        if (!this.guardWrite('clearDrawingObjects')) return;
        this.binding?.clearDrawingObjects(layer);
    }

    /** The current Y.Doc (for constructing a layer's `Y.UndoManager`), or null. */
    getYDoc(): Y.Doc | null {
        return this.yDoc;
    }

    /**
     * The provider's `Awareness` instance for ephemeral intra-stroke preview, or null
     * in offline/draft mode (no provider) — the drawing canvas then works locally
     * (draw/undo) without live remote preview.
     */
    getAwareness(): Awareness | null {
        return this.provider?.awareness ?? null;
    }

    /**
     * Read current Y.Doc state snapshot.
     */
    getSnapshot(): SyncedLessonState | null {
        return this.binding?.getSnapshot() ?? null;
    }

    /**
     * Export the current document's display state for a handoff — see
     * `LessonYjsBinding.exportDisplayState` for the contract (schema-driven;
     * `LESSON_SCOPED_KEYS` and the primary-slide history never carry). Returns
     * `null` when no document exists. Only the lifecycle effects should call this
     * (the stop effect exports before `destroy()` + `initializeOffline(snapshot)`).
     */
    exportDisplayState(): DisplayStateSnapshot | null {
        return this.binding?.exportDisplayState() ?? null;
    }

    /**
     * Snapshot of the primary-slide history accumulated during the current lesson.
     * Returns `[]` when the service is not initialized (e.g. before lesson start).
     *
     * Used by the bridge effect to feed `startWith()` after late-join hydration.
     * Application code should select from the mirrored NgRx slice
     * (`selectPrimarySlideHistory`) instead of calling this directly.
     */
    getPrimarySlideHistorySnapshot(): PrimarySlideHistoryEntry[] {
        return this.binding?.getPrimarySlideHistorySnapshot() ?? [];
    }

    /**
     * Whether the service has a Y.Doc + binding ready (online or offline mode).
     * Used by `guardWrite` — offline writes are valid.
     */
    isInitialized(): boolean {
        return this.yDoc !== null && this.binding !== null;
    }

    /**
     * Whether the current document is a synced document FOR the given lesson.
     * False in offline (`'draft'`) mode, after `destroy()`, and — critically —
     * when a synced document from a *different* lesson is still around: a stale
     * provider must not suppress initialization for the lesson actually being
     * joined. Use this (not `isInitialized()`) to decide whether a lesson
     * restore is still needed.
     */
    isSyncedToLesson(lessonId: string): boolean {
        return this.provider !== null && this.syncedLessonId === lessonId;
    }

    /**
     * Destroy Y.Doc, provider, and binding. Disables hydration.
     */
    destroy(): void {
        this.hydratedSubject.next(false);

        this.syncedLessonId = null;
        this.provider?.disconnect();
        this.provider = null;

        this.binding?.destroy();
        this.binding = null;

        this.yDoc?.destroy();
        this.yDoc = null;
    }

    private guardWrite(methodName: string): boolean {
        if (!this.isInitialized()) {
            this.log.warn(`${methodName}() called before initialization — write dropped`);
            return false;
        }
        if (!this.isWriter()) {
            this.log.error(`${methodName}() called by ${this.clientType} client — no write permission`);
            return false;
        }
        return true;
    }
}
